import { useState, useEffect, type FormEvent } from 'react';
import { Calendar, dateFnsLocalizer, Views, type View } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { de } from 'date-fns/locale';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { useAuth } from '../contexts/AuthContext';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const locales = { 'de': de };
const localizer = dateFnsLocalizer({
  format, parse, startOfWeek, getDay, locales,
});

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

const Icons = {
  X: () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>,
  Users: () => <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  Calendar: () => <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M8 2v4"/><path d="M16 2v4"/><rect width="18" height="18" x="3" y="4" rx="2"/><path d="M3 10h18"/></svg>,
  LogOut: () => <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" x2="9" y1="12" y2="12"/></svg>,
  Refresh: () => <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M8 16H3v5"/></svg>,
};

export default function SuperAdminDashboard() {
  const { user, loading, signIn, signOut: authSignOut, getAuthHeader } = useAuth();

  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [users, setUsers] = useState<any[]>([]);
  const [usersWithStats, setUsersWithStats] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [events, setEvents] = useState<any[]>([]);
  const [view, setView] = useState<View>(Views.WEEK);
  const [date, setDate] = useState(new Date());
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<any | null>(null);
  const [activeView, setActiveView] = useState<'calendar' | 'developer'>('developer');

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();
    setLoginError('');
    try {
      await signIn(loginEmail.trim().toLowerCase(), loginPassword);
    } catch (err: any) {
      setLoginError(err.code === 'auth/invalid-credential' ? 'Falsche Anmeldedaten.' : err.message);
    }
  };

  const handleLogout = async () => {
    await authSignOut();
    setUsers([]);
    setEvents([]);
  };

  const fetchUsers = async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API_BASE_URL}/super-admin/users`, {
        headers: await getAuthHeader()
      });
      setUsers(res.data);
    } catch (err) {
      console.error('[SUPER-ADMIN] Failed to fetch users', err);
    }
  };

  const fetchUsersWithStats = async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API_BASE_URL}/super-admin/users-with-stats`, {
        headers: await getAuthHeader()
      });
      setUsersWithStats(res.data);
    } catch (err) {
      console.error('[SUPER-ADMIN] Failed to fetch users with stats', err);
    }
  };

  const fetchUserDetails = async (ownerId: string) => {
    if (!user) return;
    try {
      const res = await axios.get(`${API_BASE_URL}/super-admin/users/${ownerId}`, {
        headers: await getAuthHeader()
      });
      return res.data;
    } catch (err) {
      console.error('[SUPER-ADMIN] Failed to fetch user details', err);
      return null;
    }
  };

  const fetchEvents = async (ownerId?: string) => {
    if (!user) return;
    try {
      const targetOwnerId = ownerId || selectedUser;
      const authHeader = await getAuthHeader();
      if (!targetOwnerId) {
        const allEvents: any[] = [];
        for (const u of users) {
          try {
            const res = await axios.get(`${API_BASE_URL}/super-admin/users/${u.owner_id}/events`, {
              params: { start: '2024-01-01', end: '2026-01-01' },
              headers: authHeader
            });
            allEvents.push(...res.data);
          } catch (err) {
            console.error(`[SUPER-ADMIN] Failed to fetch events for ${u.owner_id}`, err);
          }
        }
        setEvents(allEvents);
      } else {
        const res = await axios.get(`${API_BASE_URL}/super-admin/users/${targetOwnerId}/events`, {
          params: { start: '2024-01-01', end: '2026-01-01' },
          headers: authHeader
        });
        setEvents(res.data);
      }
    } catch (err) {
      console.error('[SUPER-ADMIN] Failed to fetch events', err);
    }
  };

  const handleRegenerateApiKey = async (ownerId: string) => {
    if (!user || !confirm("Möchten Sie den API-Key wirklich regenerieren? Der alte Key wird ungültig!")) return;
    try {
      const res = await axios.post(`${API_BASE_URL}/super-admin/users/${ownerId}/regenerate-api-key`, {}, {
        headers: await getAuthHeader()
      });
      alert(`Neuer API-Key: ${res.data.api_key}\n\nBitte notieren Sie sich diesen Key!`);
      fetchUsers();
      if (editingUser && editingUser.owner_id === ownerId) {
        setEditingUser({ ...editingUser, api_key: res.data.api_key });
      }
    } catch (err: any) {
      alert(err.response?.data?.error || "Fehler beim Regenerieren des API-Keys");
    }
  };

  useEffect(() => {
    if (user) {
      fetchUsers();
      fetchUsersWithStats();
    }
  }, [user]);

  useEffect(() => {
    if (user && users.length > 0) {
      fetchEvents();
    }
  }, [user, selectedUser, users]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gray-300 border-t-gray-700 rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Super Admin</h1>
            <p className="text-gray-500">Verwaltung aller Benutzer</p>
          </div>
          
          <div className="bg-white p-8 rounded-2xl shadow-[0_2px_20px_rgba(0,0,0,0.04)] border border-gray-100">
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">E-Mail</label>
                <input
                  type="email"
                  className="w-full bg-gray-50 border border-gray-200 text-gray-900 text-sm px-3 py-2.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-900 transition-all"
                  value={loginEmail}
                  onChange={e => setLoginEmail(e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Passwort</label>
                <input
                  type="password"
                  className="w-full bg-gray-50 border border-gray-200 text-gray-900 text-sm px-3 py-2.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-900 transition-all"
                  value={loginPassword}
                  onChange={e => setLoginPassword(e.target.value)}
                  required
                />
              </div>
              {loginError && <p className="text-red-500 text-xs">{loginError}</p>}
              <motion.button
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                type="submit"
                className="w-full bg-gray-900 text-white font-medium text-sm py-2.5 rounded-lg shadow-md hover:bg-gray-800 transition-all"
              >
                Anmelden
              </motion.button>
            </form>
          </div>
        </motion.div>
      </div>
    );
  }

  const mappedEvents = events.map((ev: any) => ({
    id: ev.id,
    title: `${ev.title} (${ev.owner_id})`,
    start: new Date(ev.start),
    end: new Date(ev.end),
    resourceId: ev.resourceId,
    status: ev.status,
    owner_id: ev.owner_id
  }));

  return (
    <div className="flex h-screen bg-[#FDFDFD] font-sans text-gray-900 overflow-hidden">
      {/* Sidebar */}
      <aside className="w-[240px] bg-[#FAFAFA] border-r border-gray-100 flex flex-col">
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">Super Admin</h2>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4">
          <div className="mb-4">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">User Filter</label>
            <select
              className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm"
              value={selectedUser || ''}
              onChange={e => setSelectedUser(e.target.value || null)}
            >
              <option value="">Alle User</option>
              {users.map(user => (
                <option key={user.owner_id} value={user.owner_id}>
                  {user.business_name || user.contact_email || user.owner_id}
                </option>
              ))}
            </select>
          </div>
          
          <div className="space-y-2">
            {users.map(user => (
              <div
                key={user.owner_id}
                className={cn(
                  "p-3 rounded-lg cursor-pointer transition-colors border",
                  selectedUser === user.owner_id ? "bg-gray-900 text-white border-gray-900" : "bg-white hover:bg-gray-50 border-gray-200"
                )}
                onClick={async () => {
                  setSelectedUser(user.owner_id);
                  const details = await fetchUserDetails(user.owner_id);
                  setEditingUser(details || user);
                  setShowUserModal(true);
                }}
              >
                <div className="font-medium text-sm">{user.business_name || user.contact_email || user.owner_id}</div>
                <div className="text-xs opacity-70 mt-1">{user.owner_id}</div>
              </div>
            ))}
          </div>
        </div>
        
        <div className="p-4 border-t border-gray-100">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
          >
            <Icons.LogOut />
            Abmelden
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        <div className="p-6 border-b border-gray-100 bg-white">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {activeView === 'developer' ? 'Developer Dashboard' : 'Termine Übersicht'}
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                {activeView === 'developer' 
                  ? 'Übersicht aller Benutzer mit technischen Details'
                  : selectedUser 
                    ? `User: ${users.find(u => u.owner_id === selectedUser)?.business_name || selectedUser}` 
                    : 'Alle User'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex gap-2 bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setActiveView('developer')}
                  className={cn(
                    "px-4 py-2 text-sm rounded-md transition-colors",
                    activeView === 'developer' 
                      ? "bg-white text-gray-900 shadow-sm" 
                      : "text-gray-600 hover:text-gray-900"
                  )}
                >
                  Developer
                </button>
                <button
                  onClick={() => setActiveView('calendar')}
                  className={cn(
                    "px-4 py-2 text-sm rounded-md transition-colors",
                    activeView === 'calendar' 
                      ? "bg-white text-gray-900 shadow-sm" 
                      : "text-gray-600 hover:text-gray-900"
                  )}
                >
                  Kalender
                </button>
              </div>
              <button
                onClick={() => {
                  fetchUsers();
                  fetchUsersWithStats();
                  if (activeView === 'calendar') fetchEvents();
                }}
                className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex items-center gap-2"
              >
                <Icons.Refresh />
                Aktualisieren
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 p-6 overflow-auto">
          {activeView === 'developer' ? (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Owner ID</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Firmenname</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">API-Key</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">API Base URL</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Termine</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Kunden</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Admins</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Erstellt</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {usersWithStats.map((user) => (
                      <tr key={user.owner_id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3">
                          <code className="text-xs font-mono text-gray-900 bg-gray-50 px-2 py-1 rounded">
                            {user.owner_id}
                          </code>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                          {user.business_name || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {user.contact_email || '-'}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <code className="text-xs font-mono text-gray-700 bg-gray-50 px-2 py-1 rounded max-w-[200px] truncate">
                              {user.api_key || 'N/A'}
                            </code>
                            {user.api_key && (
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(user.api_key);
                                  alert('API-Key kopiert!');
                                }}
                                className="text-xs text-gray-500 hover:text-gray-900 px-2 py-1 hover:bg-gray-100 rounded transition-colors"
                                title="Kopieren"
                              >
                                📋
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <code className="text-xs font-mono text-gray-600 bg-gray-50 px-2 py-1 rounded">
                            {API_BASE_URL.replace('/api', '')}
                          </code>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {user.appointment_count || 0}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {user.client_count || 0}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                            {user.admin_count || 0}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500">
                          {user.created_at 
                            ? new Date(user.created_at).toLocaleDateString('de-DE', { 
                                year: 'numeric', 
                                month: 'short', 
                                day: 'numeric' 
                              })
                            : '-'}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={async () => {
                              const details = await fetchUserDetails(user.owner_id);
                              setEditingUser(details || user);
                              setShowUserModal(true);
                            }}
                            className="text-xs text-gray-600 hover:text-gray-900 px-2 py-1 hover:bg-gray-100 rounded transition-colors"
                          >
                            Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {usersWithStats.length === 0 && (
                  <div className="p-8 text-center text-gray-500">
                    Keine Benutzer gefunden
                  </div>
                )}
              </div>
              
              {/* API Endpoints Info */}
              <div className="border-t border-gray-200 p-4 bg-gray-50">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">API Endpoints</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                  <div>
                    <code className="text-gray-600">POST {API_BASE_URL}/availability</code>
                  </div>
                  <div>
                    <code className="text-gray-600">POST {API_BASE_URL}/suggest-appointments</code>
                  </div>
                  <div>
                    <code className="text-gray-600">POST {API_BASE_URL}/request-code</code>
                  </div>
                  <div>
                    <code className="text-gray-600">POST {API_BASE_URL}/verify-code</code>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Alle öffentlichen Endpoints benötigen den Header: <code className="bg-white px-1 rounded">X-API-Key: &lt;api_key&gt;</code>
                </p>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 h-full">
              <Calendar
                localizer={localizer}
                events={mappedEvents}
                startAccessor="start"
                endAccessor="end"
                view={view}
                onView={setView}
                date={date}
                onNavigate={setDate}
                style={{ height: '100%' }}
                eventPropGetter={(event) => {
                  const bgColor = event.owner_id === selectedUser ? '#1f2937' : '#6b7280';
                  return {
                    style: {
                      backgroundColor: bgColor,
                      borderColor: bgColor,
                      color: 'white'
                    }
                  };
                }}
              />
            </div>
          )}
        </div>

        {/* User Details Modal */}
        <AnimatePresence>
          {showUserModal && editingUser && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-gray-900/20 backdrop-blur-sm"
                onClick={() => setShowUserModal(false)}
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white w-full max-w-2xl rounded-2xl shadow-xl border border-gray-100 relative z-10 overflow-hidden"
              >
                <div className="px-6 py-5 border-b border-gray-50 flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-900">User Details</h3>
                  <button onClick={() => setShowUserModal(false)} className="p-1.5 rounded-lg hover:bg-gray-100"><Icons.X /></button>
                </div>
                <div className="p-6 space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Owner ID</label>
                    <div className="text-sm text-gray-900 font-mono">{editingUser.owner_id}</div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Email</label>
                    <div className="text-sm text-gray-900">{editingUser.contact_email}</div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Firmenname</label>
                    <div className="text-sm text-gray-900">{editingUser.business_name || '-'}</div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">API-Key</label>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 font-mono text-xs bg-gray-50 p-2 rounded border border-gray-200 break-all">
                        {editingUser.api_key || 'Nicht verfügbar'}
                      </div>
                      <button
                        onClick={() => handleRegenerateApiKey(editingUser.owner_id)}
                        className="px-3 py-2 text-xs bg-gray-900 text-white rounded-lg hover:bg-gray-800"
                      >
                        Neu generieren
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}

